import { ErrorState, Select, Tip } from '@ly360/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, Info, Lock, Send } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { handleLabel } from '../crm/handles';
import { Link } from 'react-router';
import { useFormat } from '@ly360/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ComposerTranslate, useCanTranslate } from './Translate';
import { renderTemplate, type ConversationDetail } from './types';

const MAX: Record<string, number> = { whatsapp: 4096 };

/**
 * Reply composer. Each draft carries an idempotency key, so a double click or a network retry can never send the
 * same reply twice. The reply rules (window, templates, channel support) come from the server.
 */
export function Composer({ detail, onTyping }: { detail: ConversationDetail; onTyping: () => void }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const fmt = useFormat();
  const qc = useQueryClient();
  const policy = detail.replyPolicy;
  const channel = detail.conversation.channel;
  const channelName = t(`channels.${channel}`);
  const canReply = can('conversations:reply') && policy.mode !== 'unavailable';
  const [mode, setMode] = useState<'reply' | 'note'>(canReply ? 'reply' : 'note');
  const [text, setText] = useState('');
  const canTranslate = useCanTranslate();
  // The reply box starts at two lines and grows with the text (up to a limit), keeping the thread visible.
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 220)}px`;
  }, [text]);
  const [templateId, setTemplateId] = useState('');
  const [params, setParams] = useState<string[]>([]);
  const draftKey = useRef(crypto.randomUUID());
  const lastTyping = useRef(0);
  const conversationId = detail.conversation.id;

  useEffect(() => {
    setMode(canReply ? 'reply' : 'note');
    setText('');
    setTemplateId('');
    draftKey.current = crypto.randomUUID();
  }, [conversationId, canReply]);

  const template = detail.templates.find((x) => x.id === templateId);
  // Comments and reviews are answered on the post for everyone, or (Instagram, Facebook) privately.
  const engagement = detail.conversation.kind === 'comment' || detail.conversation.kind === 'review';
  const [replyMode, setReplyMode] = useState<'public' | 'private'>('public');
  useEffect(() => setReplyMode('public'), [conversationId]);
  const useTemplate = mode === 'reply' && policy.mode === 'template';

  const send = useMutation({
    mutationFn: () =>
      api(`/v1/conversations/${conversationId}/messages`, {
        method: 'POST',
        idempotencyKey: draftKey.current,
        body: JSON.stringify(
          mode === 'note'
            ? { text, internal: true }
            : useTemplate
              ? { template: { id: templateId, params } }
              : engagement
                ? { text, mode: replyMode }
                : { text },
        ),
      }),
    onSuccess: () => {
      setText('');
      setParams([]);
      setTemplateId('');
      draftKey.current = crypto.randomUUID();
      for (const k of ['messages', 'conversation', 'conversations', 'timeline'])
        void qc.invalidateQueries({ queryKey: [k] });
    },
  });

  const ready =
    mode === 'note'
      ? text.trim().length > 0
      : useTemplate
        ? !!template && params.length === template.variables && params.every((p) => p.trim())
        : text.trim().length > 0;
  const submit = () => {
    if (ready && !send.isPending) send.mutate();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      submit();
    }
  };
  const typing = (v: string) => {
    setText(v);
    if (Date.now() - lastTyping.current > 3_000) {
      lastTyping.current = Date.now();
      onTyping();
    }
  };
  const max = MAX[channel];

  return (
    <div className={`composer${mode === 'note' ? ' is-note' : ''}`} data-testid="composer">
      <div className="composer-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={mode === 'reply'}
          disabled={!canReply}
          onClick={() => setMode('reply')}
        >
          {t('inbox.composer.reply')}
        </button>
        <button role="tab" aria-selected={mode === 'note'} onClick={() => setMode('note')}>
          <Lock size={13} />
          {t('inbox.composer.note')}
        </button>
        <span className="spacer" />
        {mode === 'reply' && policy.mode === 'free' && policy.windowClosesAt && !engagement && (
          <Tip
            label={t('inbox.composer.windowOpen', { rel: fmt.relative(policy.windowClosesAt) })}
            side="end"
          >
            <span className="composer-chip" data-testid="reply-window">
              <Clock size={12} aria-hidden="true" />
              {fmt.relative(policy.windowClosesAt)}
            </span>
          </Tip>
        )}
        {mode === 'reply' && detail.recipient && !engagement && (
          <span
            className="faint composer-to"
            dir="auto"
            title={t('inbox.composer.to', {
              value: handleLabel(t, detail.recipient.type, detail.recipient.value),
            })}
          >
            {t('inbox.composer.to', { value: handleLabel(t, detail.recipient.type, detail.recipient.value) })}
          </span>
        )}
      </div>

      {policy.mode === 'unavailable' && (
        <div className="notice" role="note">
          <Info size={16} />
          <span>
            {channel === 'voice'
              ? t('inbox.composer.voiceHint')
              : t(`inbox.composer.unavailable.${policy.reason}`, {
                  channel: channelName,
                  phase: policy.replyPhase,
                })}{' '}
            {policy.reason === 'no_account' && can('integrations:manage') && (
              <Link to="/admin/integrations">{t('inbox.composer.goIntegrations')}</Link>
            )}
          </span>
        </div>
      )}

      {mode === 'reply' && engagement && canReply && (
        <div className="reply-mode" data-testid="reply-mode">
          {detail.conversation.canReplyPrivately ? (
            <div className="segmented" role="group" aria-label={t('inbox.replyMode.label')}>
              {(['public', 'private'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={replyMode === m}
                  onClick={() => setReplyMode(m)}
                  data-testid={`reply-${m}`}
                >
                  {t(`inbox.replyMode.${m}`)}
                </button>
              ))}
            </div>
          ) : null}
          <span className="faint">
            {replyMode === 'private'
              ? t('inbox.replyMode.privateHint')
              : detail.conversation.kind === 'review'
                ? t('inbox.replyMode.reviewHint')
                : t('inbox.replyMode.publicHint')}
          </span>
        </div>
      )}

      {useTemplate ? (
        <div className="composer-template">
          <div className="notice warn" role="note">
            <Info size={16} />
            {t('inbox.composer.windowClosed')}
          </div>
          {detail.templates.length === 0 ? (
            <p className="faint">{t('inbox.composer.noTemplates')}</p>
          ) : (
            <>
              <label className="sr-only" htmlFor="tpl-select">
                {t('inbox.composer.chooseTemplate')}
              </label>
              <Select
                id="tpl-select"
                value={templateId}
                onChange={(v) => {
                  setTemplateId(v);
                  const tpl = detail.templates.find((x) => x.id === v);
                  setParams(Array.from({ length: tpl?.variables ?? 0 }, () => ''));
                }}
                searchable
                placeholder={t('inbox.composer.chooseTemplate')}
                options={detail.templates.map((tpl) => ({
                  value: tpl.id,
                  label: `${tpl.name} · ${tpl.language}`,
                  hint: tpl.body,
                }))}
              />
              {template && (
                <>
                  <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                    {params.map((p, i) => (
                      <div className="field" key={i}>
                        <label htmlFor={`tpl-p${i}`}>{t('inbox.composer.param', { n: i + 1 })}</label>
                        <input
                          id={`tpl-p${i}`}
                          className="input"
                          dir="auto"
                          value={p}
                          onChange={(e) => setParams(params.map((x, j) => (j === i ? e.target.value : x)))}
                        />
                      </div>
                    ))}
                  </div>
                  <div
                    className="bubble out"
                    style={{ maxWidth: '100%' }}
                    dir="auto"
                    aria-label={t('inbox.composer.preview')}
                  >
                    {renderTemplate(template.body, params)}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      ) : (
        (mode === 'note' || policy.mode === 'free') && (
          <>
            <label className="sr-only" htmlFor="composer-text">
              {mode === 'note' ? t('inbox.composer.note') : t('inbox.composer.reply')}
            </label>
            <textarea
              id="composer-text"
              className="textarea"
              dir="auto"
              rows={2}
              ref={box}
              value={text}
              maxLength={max}
              placeholder={
                mode === 'note'
                  ? t('inbox.composer.notePlaceholder')
                  : engagement
                    ? t('inbox.replyMode.placeholder')
                    : t('inbox.composer.placeholder', { channel: channelName })
              }
              onChange={(e) => typing(e.target.value)}
              onKeyDown={onKey}
            />
          </>
        )
      )}

      {send.isError && <ErrorState error={send.error} />}
      <div className="composer-actions">
        <div className="action-bar">
          {canTranslate && mode === 'reply' && policy.mode === 'free' && !useTemplate && (
            <ComposerTranslate text={text} onChange={typing} />
          )}
        </div>
        {/* The length only matters near the channel's limit. */}
        {max && mode === 'reply' && !useTemplate && text.length > max * 0.8 && (
          <span className="faint">{t('inbox.composer.chars', { count: text.length, max })}</span>
        )}
        <Tip label={t('inbox.composer.sendHint')} side="top">
          <button
            className={`btn btn-sm ${mode === 'note' ? 'btn-ghost' : 'btn-grad'}`}
            onClick={submit}
            disabled={!ready || send.isPending}
            data-testid="send"
          >
            <Send size={14} className="icon-dir" />
            {send.isPending
              ? t('inbox.composer.sending')
              : mode === 'note'
                ? t('inbox.composer.addNote')
                : t('inbox.composer.send')}
          </button>
        </Tip>
      </div>
    </div>
  );
}
